import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { supabase } from '@/lib/supabase';
import { mockGigs, gigCategories, type Gig } from '@/mocks/gigs';
import { mapDbGig, formatGigPrice } from '@/lib/gigs';

const workHero =
  'https://readdy.ai/api/search-image?query=African%20freelancers%20working%20on%20laptops%20in%20modern%20coworking%20space%2C%20professional%20remote%20work%20environment%2C%20diverse%20team%20collaboration%2C%20warm%20natural%20lighting%2C%20clean%20minimal%20interior%2C%20editorial%20photography&width=1600&height=800&seq=work-hero-01&orientation=landscape';

type SortKey = 'recent' | 'price-asc' | 'price-desc' | 'rating-desc';

function WorkPage() {
  const [gigs, setGigs] = useState<Gig[]>(mockGigs);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('gigs')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (err) throw err;
      if (data && data.length > 0) {
        setGigs((data as unknown[]).map((row) => mapDbGig(row as never)));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les services.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = gigs.filter((gig) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      gig.title.toLowerCase().includes(q) ||
      gig.freelancer.toLowerCase().includes(q) ||
      gig.description.toLowerCase().includes(q);
    const matchesCategory = !category || gig.category === category;

    const minVal = priceMin ? Number(priceMin) : null;
    const maxVal = priceMax ? Number(priceMax) : null;
    const hasPrice = gig.price !== null;
    const matchesPrice =
      (!minVal && !maxVal) ||
      (hasPrice &&
        (!minVal || (gig.price ?? 0) >= minVal) &&
        (!maxVal || (gig.price ?? 0) <= maxVal));

    return matchesSearch && matchesCategory && matchesPrice;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'price-asc') return (a.price ?? 0) - (b.price ?? 0);
    if (sort === 'price-desc') return (b.price ?? 0) - (a.price ?? 0);
    if (sort === 'rating-desc') return (b.rating ?? 0) - (a.rating ?? 0);
    return b.postedAt.localeCompare(a.postedAt);
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={workHero}
              alt="WorldApeiron Work - marketplace freelance"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">
                WorldApeiron Work
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
                Trouvez des talents. Réalisez vos projets.
              </h1>
              <p className="text-base md:text-lg text-foreground-400 max-w-xl mb-8 leading-relaxed">
                Connectez-vous avec les meilleurs freelancers africains en développement, design,
                marketing, rédaction et bien plus. Un système d&apos;escrow sécurisé protège chaque
                transaction.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1 max-w-xl flex items-center bg-background-50 border border-background-300/60 rounded-full overflow-hidden focus-within:border-accent-400">
                  <div className="w-11 h-11 flex items-center justify-center shrink-0">
                    <i className="ri-search-line text-foreground-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un service, un freelance..."
                    className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 pr-4 outline-none"
                  />
                </div>
                <Link
                  to="/dashboard/gigs/new"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-foreground-950 font-semibold rounded-full hover:bg-accent-400 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  Proposer un service
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="work" />
        </section>

        {/* Filters */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
            <span className="text-sm font-semibold text-foreground-400">
              {filtered.length} service{filtered.length > 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <label htmlFor="work-sort" className="text-sm text-foreground-400">Trier par</label>
              <select
                id="work-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="bg-background-100 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-accent-400"
              >
                <option value="recent">Plus récents</option>
                <option value="rating-desc">Mieux notés</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  category === null
                    ? 'bg-accent-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                }`}
              >
                Toutes les catégories
              </button>
              {gigCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    category === cat
                      ? 'bg-accent-500 text-foreground-950'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-foreground-400 flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-money-dollar-circle-line" />
                Budget
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  placeholder="Min"
                  className="w-28 bg-background-100 border border-background-300/60 rounded-md px-3 py-1.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
                />
                <span className="text-foreground-400">—</span>
                <input
                  type="number"
                  min="0"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="Max"
                  className="w-28 bg-background-100 border border-background-300/60 rounded-md px-3 py-1.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
                />
                <span className="text-xs text-foreground-400 whitespace-nowrap">FCFA</span>
              </div>
              {(priceMin || priceMax) && (
                <button
                  onClick={() => {
                    setPriceMin('');
                    setPriceMax('');
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-accent-600 bg-accent-500/10 hover:bg-accent-500/20 transition-colors whitespace-nowrap"
                >
                  <i className="ri-close-line" />
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Gigs grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10">
                <i className="ri-loader-4-line text-2xl text-accent-400 animate-spin" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-6 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-500/10">
                <i className="ri-briefcase-4-line text-3xl text-accent-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Aucun service trouvé
              </h2>
              <p className="text-sm text-foreground-400 mb-6">
                Aucun service ne correspond à vos critères. Élargissez votre recherche.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {sorted.map((gig) => (
                <article
                  key={gig.id}
                  className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-accent-400/40 transition-all duration-300 flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={gig.cover}
                      alt={`Service ${gig.title}`}
                      title={`${gig.title} - ${gig.category}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-50 whitespace-nowrap">
                        {gig.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {gig.rating !== null && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-400 whitespace-nowrap">
                          <i className="ri-star-fill" />
                          {gig.rating.toFixed(1)}
                          <span className="font-normal text-foreground-400">
                            ({gig.reviewsCount})
                          </span>
                        </span>
                      )}
                      {gig.deliveryDays !== null && (
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <i className="ri-time-line" />
                          {gig.deliveryDays} jours
                        </span>
                      )}
                    </div>

                    <Link to={`/work/${gig.id}`}>
                      <h2 className="font-heading font-semibold text-foreground-50 leading-snug hover:text-accent-400 transition-colors line-clamp-2">
                        {gig.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-foreground-400 mt-1">
                      <Link
                        to={`/freelancers/${gig.ownerId}`}
                        className="hover:text-accent-400 transition-colors"
                      >
                        {gig.freelancer}
                      </Link>
                      <span className="text-foreground-500"> · {gig.location}</span>
                    </p>

                    <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                      <span className="font-semibold text-accent-400 whitespace-nowrap">
                        {formatGigPrice(gig.price)}
                      </span>
                      <Link
                        to={`/work/${gig.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-500 text-foreground-950 font-medium text-sm hover:bg-accent-400 transition-colors whitespace-nowrap"
                      >
                        Commander
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100 border-t border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Vous êtes freelance ?
            </h2>
            <p className="text-foreground-400 mb-8">
              Proposez vos services et développez votre activité sur WorldApeiron Work. Touchez des
              clients partout en Afrique et soyez payé en toute sécurité grâce à l&apos;escrow.
            </p>
            <Link
              to="/dashboard/gigs/new"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-circle-line text-lg" />
              Devenir freelance
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default WorkPage;